import React, { useEffect, useState } from "react"
import css from "../ChatHistory.module.css"
import { useParams } from "react-router-dom"
import { RouteInfo } from "./RouteView"
import MapView from "./MapView"
import { isMobile } from "@/utils/detectDevice"
import classNames from "classnames"
import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { Location } from "@/reducers/locationsSlice"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import ReadOnlyRouteStopList from "@/components/maps/ReadOnlyRouteStopList"
import { getAPIBaseUrl } from "@/lib/routeStateUtils"
import { useSharedRouteSettings } from "@/hooks/useSharedRouteSettings"

export function convertToRoute2g(response: any[], jid: string): Route2g {
  const stops = response.map((stop) => {
    const location: Location = {
      lat: stop.location?.lat?.toString(),
      lon: stop.location?.lng?.toString(),
      country: stop.location?.country || undefined,
      countryCode: stop.location?.country_code || undefined,
      locality: stop.location?.locality || undefined,
      street: stop.location?.street || undefined,
      postalCode: stop.location?.postal_code || undefined,
      formattedAddress: stop.location?.formatted_address || undefined,
    } as unknown as Location

    const routeStop: RouteStop = {
      id: stop.id,
      by: stop.user_id,
      location,
      msgId: stop.msg_id,
      action: stop.action,
      ref: stop.reference_number,
      remaining_distance: stop.remaining_distance,
      remaining_time: stop.remaining_time
        ? parseInt(stop.remaining_time)
        : undefined,
      scheduled_arrival_time: stop.scheduled_arrival_time,
      estimated_arrival_time: stop.estimated_arrival_time,
      proximity_status: stop.proximity_status,
      timeliness_status: stop.timeliness_status,
      isApproximate: stop.is_approximate,
      shouldAlert: stop.should_alert,
      alertMetadata: stop.alert_metadata,
      isLate: stop.timeliness_status === "late",
    }

    return routeStop
  })

  return {
    jid,
    stops,
  }
}

type RouteData = RouteInfo & {
  stops: any[]
  routes?: Array<{ distance?: number; duration?: number }>
}

const SharedMap: React.FC = () => {
  const { sharedMapId } = useParams<{ sharedMapId: string }>()
  const { showLegDistance } = useSharedRouteSettings()
  const [routeData, setRouteData] = useState<RouteData | null>(null)
  const [stops, setStops] = useState<Route2g | undefined>(undefined)
  const [totalRouteDistanceMeters, setTotalRouteDistanceMeters] = useState<
    number | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchRouteData = async () => {
      if (!sharedMapId) {
        setError("No shared map ID provided")
        setIsLoading(false)
        return
      }

      try {
        const baseUrl =
          window.location.hostname !== "app.trickswift.eu"
            ? getAPIBaseUrl()
            : "https://api.trickswift.eu/production"

        const response = await fetch(
          `${baseUrl}/shared_route?secret=${sharedMapId}`,
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch route data: ${response.status}`)
        }

        const data = await response.json()
        setRouteData(data)
        setStops(convertToRoute2g(data.stops, "shared"))

        const stopsHaveDistances = (data.stops as any[]).some(
          (s) => s.remaining_distance != null,
        )
        if (!stopsHaveDistances) {
          const routeDistance = data.routes?.[0]?.distance
          setTotalRouteDistanceMeters(
            typeof routeDistance === "number" ? routeDistance : undefined,
          )
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch route data",
        )
      } finally {
        setIsLoading(false)
      }
    }

    fetchRouteData()
  }, [sharedMapId])

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        Loading map data...
      </div>
    )
  }

  if (error || !routeData) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center p-4 text-center">
        <h2 className="mb-2 text-xl font-bold">Unable to load map</h2>
        <p className="text-gray-600 dark:text-gray-400">
          {error || "Map data unavailable"}
        </p>
      </div>
    )
  }

  if (isMobile) {
    const mobileMapHeight = window.innerHeight
    return (
      <div
        className={css.mobileMapContainer}
        style={{ flex: 1, width: "100%" }}
      >
        <div style={{ height: `${mobileMapHeight}px`, width: "100%" }}>
          <div
            id="mobileMap"
            className={classNames(css.map, "dark:bg-gray-900")}
            style={{
              width: window.innerWidth + "px",
              height: window.innerHeight + "px",
            }}
          >
            <MapView
              sharedStops={stops}
              sharedRoute={routeData}
              driverJid="shared"
              isFullScreen
              isSharedMap
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 h-screen w-screen">
      <ResizablePanelGroup
        direction="horizontal"
        className="max-w-screen min-h-screen"
      >
        <ResizablePanel defaultSize={25} maxSize={50} minSize={25}>
          <div className="flex h-full flex-col items-start justify-start p-3">
            {stops && stops.stops.length > 0 && (
              <ReadOnlyRouteStopList
                routeStops={stops.stops}
                showLegDistance={showLegDistance}
                totalRouteDistanceMeters={totalRouteDistanceMeters}
                legs={routeData.routes?.[0]?.legs}
              />
            )}
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={75}>
          <div className="relative flex h-full items-center justify-center">
            <MapView
              sharedStops={stops}
              sharedRoute={routeData}
              driverJid="shared"
              isFullScreen
              isSharedMap
            />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}

export default SharedMap
